/**
 * Route table — single source of truth for navigation paths.
 * If a route is added or renamed, update this file first.
 */

export const ROUTES = {
  landing: '/',
  login: '/login',
  signup: '/signup',
  profileBuilder: '/profile-builder',
  authCallback: '/auth/callback',
  /** World hop — start Passport SSO on this origin so a known login can land silently. */
  authHop: '/auth/hop',
  forgot: '/auth/forgot',
  reset: '/auth/reset',
  mfaChallenge: '/auth/mfa',
  logout: '/logout',
  verify: '/verify',
  verifyAge: '/verify-age',
  feed: '/feed',
  grid: '/grid',
  map: '/map',
  profile: (handle = ':handle') => `/profile/${handle}`,
  messages: '/messages',
  /** The ecosystem's one front door to Hottie — every sibling site links here. */
  hottie: '/hottie',
  world: '/world',
  vipPreview: '/vip-preview',
  settings: '/settings',
  legal: (doc = ':doc') => `/legal/${doc}`,

  /**
   * HOTTR.vip — directory-only module. Routes are namespaced under /vip/*
   * so they coexist with hottr.app's main surface without collision.
   * See docs/HOTTR_VIP_PRODUCT_RULES.md before editing this module.
   */
  vip: {
    landing: '/vip',
    access: '/vip/access',
    roster: '/vip/roster',
    profile: (handle = ':handle') => `/vip/profile/${handle}`,
    contact: (handle = ':handle') => `/vip/contact/${handle}`,
    providerApply: '/vip/provider/apply',
    providerProfile: '/vip/provider/profile',
    verify: '/vip/verify',
    world: '/vip/world',
  },

  /**
   * /admin/* — moderator-only. Gated by `requireModerator` on ProtectedRoute,
   * which checks membership in `public.hottr_moderators`.
   */
  admin: {
    queue: '/admin/queue',
    safety: '/admin/safety',
    revenue: '/admin/revenue',
    launchGates: '/admin/launch-gates',
    secrets: '/admin/secrets',
  },

  /** Plans page — public surface that gates to checkout on click. */
  plans: '/plans',

  /**
   * HOTTR.tv — streaming + creator surface. Namespaced under /tv/*.
   * See docs/HOTTR_TV_PRODUCT_RULES.md before editing this module.
   * Distinct from VIP: TV is content unlock + subscription, never a
   * payment for any provider service.
   */
  tv: {
    browse: '/tv',
    watch: (id = ':id') => `/tv/watch/${id}`,
    series: (slug = ':slug') => `/tv/series/${slug}`,
    creator: (handle = ':handle') => `/tv/creator/${handle}`,
    library: '/tv/library',
    locked: (slug = ':slug') => `/tv/locked/${slug}`,
    profile: '/tv/profile',
    account: '/tv/account',
    creatorApply: '/tv/creator/apply',
    world: '/tv/world',
  },
} as const;

/**
 * Surfaces live in production. `.vip` and `.tv` are built but data-stubbed
 * (no backend yet) — hidden from nav and their routes are not mounted until
 * their backends ship. Consumed by AppSwitcher (nav pills) and AppRoutes
 * (route mounting). Flip to true to re-enable a surface.
 */
export const LIVE_SURFACES: Record<'app' | 'vip' | 'tv', boolean> = {
  app: true,
  vip: false,
  tv: false,
};
